import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber, formatFull, formatDate } from "@/lib/format";
import { Slider } from "@/components/ui/slider";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  ComposedChart, Line, Legend,
} from "recharts";
import {
  GaugeIcon, CircleNotchIcon, CurrencyDollarIcon, TrendUpIcon, PulseIcon,
  MedalIcon, LightningIcon, TargetIcon, CheckCircleIcon, HourglassMediumIcon,
  CalendarBlankIcon, ClockIcon, FilmSlateIcon, CopySimpleIcon, ProhibitIcon, FireIcon, RepeatIcon, TimerIcon, RocketLaunchIcon, CaretDownIcon,
  CompassIcon, StorefrontIcon, UsersThreeIcon, CalculatorIcon,
} from "@phosphor-icons/react";

const compColor = (lvl = "") => {
  const c = (lvl || "").toLowerCase();
  if (c === "low") return "#16C784";
  if (c === "medium") return "#FFB800";
  if (c === "high") return "#FF4D4D";
  return "#A0A0AB";
};

const fmtMoney = (n) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  if (n >= 1000) return `$${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}K`;
  return `$${n.toFixed(n < 100 ? 2 : 0)}`;
};

const RevenueCalculator = ({ rpmLow, rpmHigh, defaultViews = 100000 }) => {
  const [views, setViews] = useState(defaultViews);
  const lo = rpmLow != null ? (views / 1000) * rpmLow : null;
  const hi = rpmHigh != null ? (views / 1000) * rpmHigh : null;
  const fmtViews = (v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v / 1000)}K` : `${v}`);
  return (
    <div data-testid="revenue-calculator" className="mt-4 border border-[#A3E635]/25 bg-[#0C0C0E] p-4">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A3E635] mb-3">
        <CalculatorIcon size={14} weight="fill" /> Revenue Calculator
      </div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider text-[#5a5a63]">Views / month</span>
        <span data-testid="calc-views" className="font-rajdhani font-bold text-base text-white">{fmtViews(views)}</span>
      </div>
      <input
        data-testid="calc-slider"
        type="range" min={0} max={5000000} step={10000}
        value={views}
        onChange={(e) => setViews(Number(e.target.value))}
        className="w-full accent-[#A3E635] cursor-pointer"
      />
      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="border border-[#222226] bg-[#050505] p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">Est. Monthly (low)</div>
          <div data-testid="calc-low" className="font-rajdhani font-bold text-xl text-[#A0A0AB]">{fmtMoney(lo)}</div>
        </div>
        <div className="border border-[#A3E635]/30 bg-[#050505] p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">Est. Monthly (high)</div>
          <div data-testid="calc-high" className="font-rajdhani font-bold text-xl text-[#A3E635]">{fmtMoney(hi)}</div>
        </div>
      </div>
      <p className="font-mono text-[9px] text-[#5a5a63] mt-2 leading-relaxed">
        Ad-revenue estimate = views ÷ 1,000 × niche RPM (${rpmLow ?? "?"}–${rpmHigh ?? "?"}). A rough ballpark, not your actual AdSense.
      </p>
    </div>
  );
};

const gradeColor = (g = "") => {
  const c = g.toUpperCase();
  if (c === "A") return "#A3E635";
  if (c === "B") return "#FFFFFF";
  if (c === "C") return "#A0A0AB";
  return "#16C784";
};
const potColor = (p = "") =>
  p.toLowerCase() === "high" ? "#A3E635" : p.toLowerCase() === "medium" ? "#FFFFFF" : "#16C784";
const potentialColor = (s = 0) =>
  s >= 80 ? "#A3E635" : s >= 65 ? "#16C784" : s >= 50 ? "#FFFFFF" : "#A0A0AB";

// Mon-first day labels; JS getDay() is 0=Sun..6=Sat
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const jsDayToIdx = (d) => (d + 6) % 7; // Sun(0)->6, Mon(1)->0 ...
const fmtHour = (h) => new Date(2020, 0, 1, h).toLocaleTimeString([], { hour: "numeric" });
const localTzLabel = () => {
  const parts = new Date().toLocaleTimeString([], { timeZoneName: "short" }).split(" ");
  return parts[parts.length - 1];
};

// Compute posting window in the viewer's LOCAL time zone from real video timestamps.
const computeLocalPosting = (videos) => {
  const vids = (videos || []).filter((v) => v.published_at);
  if (!vids.length) return null;
  const dayBuckets = Array.from({ length: 7 }, () => []);
  const hourBuckets = {};
  vids.forEach((v) => {
    const d = new Date(v.published_at);
    dayBuckets[jsDayToIdx(d.getDay())].push(v.view_count);
    (hourBuckets[d.getHours()] = hourBuckets[d.getHours()] || []).push(v.view_count);
  });
  const avg = (a) => (a.length ? Math.round(a.reduce((s, x) => s + x, 0) / a.length) : 0);
  const day_stats = DAY_LABELS.map((day, i) => ({
    day,
    avg_views: avg(dayBuckets[i]),
    count: dayBuckets[i].length,
  }));
  const withData = day_stats.filter((d) => d.count > 0);
  const best_day = withData.length
    ? withData.reduce((a, b) => (b.avg_views > a.avg_views ? b : a)).day
    : null;
  const hourRanked = Object.entries(hourBuckets)
    .map(([h, arr]) => ({ h: Number(h), avg: avg(arr) }))
    .sort((a, b) => b.avg - a.avg);
  const best_hour = hourRanked.length ? fmtHour(hourRanked[0].h) : null;
  const top_slots = [...vids]
    .sort((a, b) => b.view_count - a.view_count)
    .slice(0, 5)
    .map((v) => {
      const d = new Date(v.published_at);
      return {
        title: v.title,
        day: DAY_LABELS[jsDayToIdx(d.getDay())],
        time: fmtHour(d.getHours()),
        views: v.view_count,
      };
    });
  return { best_day, best_hour, day_stats, top_slots };
};

// Parse a posts-per-month number from an AI frequency string like "5 videos per week".
const parsePerMonth = (freq) => {
  if (!freq) return null;
  const s = String(freq).toLowerCase();
  const match = s.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const num = parseFloat(match[1]);
  if (!num) return null;
  if (s.includes("day")) return num * 30;
  if (s.includes("month")) return num;
  return num * 4.3; // default: treat as per-week
};

// Project subscriber / watch-hour growth toward YPP and revenue 24 months beyond monetization,
// driven by per-post economics * a target posting output (posts/month). Works even when the
// channel's current momentum is ~0, so a what-if slider can model any output.
const buildProjection = (m, mon, targetPerMonth) => {
  if (!m || !mon || !m.per_post) return null;
  const goalSubs = mon.ypp_subs_required || 1000;
  const goalHours = m.watch_time?.ypp_hours_required || 4000;
  const subs0 = mon.current_subs || 0;
  const hours0 = m.watch_time?.est_total_hours || 0;
  const pp = m.per_post;
  const subsRate = (pp.subs || 0) * targetPerMonth;
  const hoursRate = (pp.watch_hours || 0) * targetPerMonth;
  const revMid = (((pp.rev_low || 0) + (pp.rev_high || 0)) / 2) * targetPerMonth;

  const monthsToSubs = subs0 >= goalSubs ? 0 : (subsRate > 0 ? (goalSubs - subs0) / subsRate : null);
  const monthsToHours = hours0 >= goalHours ? 0 : (hoursRate > 0 ? (goalHours - hours0) / hoursRate : null);
  if (monthsToSubs === null || monthsToHours === null) return null; // output too low to reach goals

  const monetizeMonth = Math.max(0, Math.ceil(Math.max(monthsToSubs, monthsToHours)));
  const bottleneck = monetizeMonth <= 0 ? null : (monthsToHours >= monthsToSubs ? "watch hours" : "subscribers");
  const endMonth = monetizeMonth + 24;
  const subsAtMon = subs0 + subsRate * monetizeMonth;

  const data = [];
  for (let mo = 0; mo <= endMonth; mo++) {
    const subs = subs0 + subsRate * mo;
    const hours = hours0 + hoursRate * mo;
    const monetized = mo >= monetizeMonth;
    let revenue = null;
    if (monetized) {
      const s = subsAtMon > 0 ? subs / subsAtMon : 1;
      revenue = Math.max(0, Math.round(revMid * s));
    }
    data.push({
      month: mo,
      label: mo === 0 ? "Now" : `+${mo}mo`,
      subsPct: Math.round(Math.min(subs / goalSubs, 2) * 100),
      hoursPct: Math.round(Math.min(hours / goalHours, 2) * 100),
      subs: Math.round(subs),
      hours: Math.round(hours),
      revenue,
    });
  }
  return { data, monetizeMonth, monetizeLabel: data[monetizeMonth]?.label, revMid, bottleneck };
};

const ProjTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  return (
    <div className="bg-[#050505] border border-[#16C784]/40 p-2.5 font-mono text-[10px] space-y-0.5">
      <div className="text-[#A3E635] font-bold mb-1">{label}</div>
      <div className="text-[#16C784]">{formatNumber(d.subs)} subs · {d.subsPct}% of 1K</div>
      <div className="text-[#60A5FA]">~{formatNumber(d.hours)} watch hrs · {d.hoursPct}% of 4K</div>
      {d.revenue != null && <div className="text-[#FFB800]">Est. ${formatNumber(d.revenue)}/mo revenue</div>}
    </div>
  );
};

const Metric = ({ label, value, sub }) => (
  <div className="border border-[#222226] bg-[#050505] p-3">
    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A0A0AB]">{label}</div>
    <div className="font-rajdhani font-bold text-xl md:text-2xl text-white mt-1">{value}</div>
    {sub && <div className="font-mono text-[10px] text-[#5a5a63] mt-0.5">{sub}</div>}
  </div>
);

const ChannelAnalysis = ({ initial, videos, channelId }) => {
  const [data, setData] = useState(initial || null);
  const [loading, setLoading] = useState(false);
  const [showMonetize, setShowMonetize] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/ai/channel-analysis?tz_offset=${-new Date().getTimezoneOffset()}${channelId ? `&channel_id=${channelId}` : ""}`);
      setData(res.data);
      toast.success("Channel intel compiled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Channel analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const m = data?.metrics;
  const mon = m?.monetization;
  const ai = data?.ai;
  // Posting window is always shown in the viewer's local time (backend also uses the tz_offset we send).
  const localPosting = useMemo(() => computeLocalPosting(videos), [videos]);
  const posting = localPosting || m?.posting;
  const tzLabel = localTzLabel();

  const currentPerMonth = m?.posts_per_month || 0;
  const recPerMonth = parsePerMonth(ai?.schedule?.optimal_frequency);
  const currentPerWeek = currentPerMonth / 4.3;
  const recPerWeek = recPerMonth ? recPerMonth / 4.3 : null;
  const havePerPost = !!(m?.per_post && m.per_post.views > 0);

  // What-if slider: posts per week (0 -> high). Default to the channel's CURRENT pace so the
  // initial projection matches the diagnostic narrative; fall back to recommended, else 3.
  const defaultPerWeek = currentPerWeek > 0 ? currentPerWeek : (recPerWeek || 3);
  const [sliderPerWeek, setSliderPerWeek] = useState(null);
  const perWeek = sliderPerWeek == null ? defaultPerWeek : sliderPerWeek;
  const sliderMax = Math.max(30, Math.ceil(currentPerWeek * 1.5));
  const targetPerMonth = perWeek * 4.3;

  const projection = useMemo(() => buildProjection(m, mon, targetPerMonth), [m, mon, targetPerMonth]);
  const basePace = useMemo(
    () => (currentPerMonth > 0 ? buildProjection(m, mon, currentPerMonth) : null),
    [m, mon, currentPerMonth]
  );
  const monthsDelta = projection && basePace ? basePace.monetizeMonth - projection.monetizeMonth : 0;
  const fmtWeek = (n) => (n >= 10 ? Math.round(n) : n.toFixed(1).replace(/\.0$/, ""));
  const fmtMonths = (mo) => {
    if (mo == null) return "—";
    if (mo <= 0) return "now";
    if (mo < 12) return `~${mo} mo`;
    return `~${(mo / 12).toFixed(1)} yr`;
  };

  return (
    <motion.div
      data-testid="channel-analysis"
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative bg-[#0C0C0E] border border-[#16C784]/30 p-5 md:p-6"
      style={{ boxShadow: "0 0 15px rgba(22, 199, 132,0.08), inset 0 1px 0 rgba(255,255,255,0.05)" }}
    >
      <div className="flex items-start justify-between gap-3 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full border border-[#16C784]/50 flex items-center justify-center">
            <GaugeIcon size={20} className="text-[#16C784]" weight="fill" />
          </div>
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635]">// Content Overwatch Intel</div>
            <h2 className="font-rajdhani font-bold text-xl tracking-wide">CHANNEL DIAGNOSTIC</h2>
          </div>
        </div>
        <button
          data-testid="analyze-channel-button"
          onClick={run}
          disabled={loading}
          className="flex items-center gap-2 bg-[#16C784] hover:bg-[#0FA968] text-white disabled:opacity-50 font-rajdhani font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors duration-200 shrink-0"
        >
          {loading ? <CircleNotchIcon size={14} className="spin-slow" /> : <PulseIcon size={14} weight="fill" />}
          {data ? "Re-run Scan" : "Run Full Scan"}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {!data ? (
          <div className="py-8 text-center">
            <GaugeIcon size={30} className="text-[#5a5a63] mx-auto mb-3" />
            <p className="font-mono text-xs text-[#5a5a63] max-w-md mx-auto">
              Run a full diagnostic for average views per post, engagement, upload cadence and monetization readiness.
            </p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Grade + headline */}
            {ai && (
              <div className="flex items-center gap-5 flex-wrap">
                <div className="relative w-20 h-20 shrink-0 rounded-full border-2 flex flex-col items-center justify-center"
                  style={{ borderColor: gradeColor(ai.grade) }}>
                  <span className="font-rajdhani font-bold text-4xl leading-none" style={{ color: gradeColor(ai.grade) }}>
                    {ai.grade}
                  </span>
                  <span className="font-mono text-[8px] text-[#5a5a63] tracking-widest mt-0.5">GRADE</span>
                </div>
                <div className="flex-1 min-w-[220px]">
                  <p className="font-rajdhani font-semibold text-lg text-white">{ai.headline}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap font-mono text-[10px] uppercase tracking-wider">
                    <span className="px-2 py-0.5 border border-[#222226] text-[#A0A0AB]">{ai.growth_stage}</span>
                    <span className="px-2 py-0.5 border" style={{ color: potColor(ai.monetization_potential), borderColor: potColor(ai.monetization_potential) + "66" }}>
                      {ai.monetization_potential} monetization potential
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Channel potential vs. age */}
            {m.potential && (
              <div data-testid="channel-potential" className="border border-[#16C784]/30 bg-[#050505] p-4"
                style={{ boxShadow: "0 0 12px rgba(22, 199, 132,0.06)" }}>
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-3">
                  <RocketLaunchIcon size={15} weight="fill" /> Channel Potential
                </div>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="relative w-20 h-20 shrink-0 rounded-full border-2 flex flex-col items-center justify-center"
                    style={{ borderColor: potentialColor(m.potential.score) }}>
                    <span className="font-rajdhani font-bold text-3xl leading-none" style={{ color: potentialColor(m.potential.score) }}>
                      {m.potential.score}
                    </span>
                    <span className="font-mono text-[8px] text-[#5a5a63] tracking-widest mt-0.5">/ 100</span>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="font-rajdhani font-bold text-lg" style={{ color: potentialColor(m.potential.score) }}>
                      {m.potential.tier}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 font-mono text-[10px] text-[#A0A0AB]">
                      <span><span className="text-[#5a5a63]">First post</span> {m.potential.first_post_date ? formatDate(m.potential.first_post_date) : "—"} · {m.potential.channel_age_label}</span>
                      {m.potential.subs_total != null && (
                        <span><span className="text-[#5a5a63]">So far</span> {formatNumber(m.potential.subs_total)} subs · {formatNumber(m.potential.views_total)} views</span>
                      )}
                      <span><span className="text-[#5a5a63]">{m.potential.annualized ? "Pace (annualized)" : "Subs / yr"}</span> ~{formatNumber(m.potential.subs_per_year)}/yr · {formatNumber(m.potential.views_per_year)} views/yr</span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#d4d4d8] mt-3 border-l-2 border-[#16C784]/40 pl-3">{m.potential.rationale}</p>
              </div>
            )}

            {/* Metrics grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Avg Views / Post" value={formatNumber(m.avg_views_per_post)} sub={formatFull(m.avg_views_per_post)} />
              <Metric label="Median Views" value={formatNumber(m.median_views_per_post)} />
              <Metric label="Engagement Rate" value={`${m.engagement_rate}%`} sub="likes+comments / views" />
              <Metric label="Upload Cadence" value={m.upload_cadence_days ? `${m.upload_cadence_days}d` : "—"} sub={m.posts_per_month ? `~${m.posts_per_month}/mo` : ""} />
              <Metric label="Avg Likes / Post" value={formatNumber(m.avg_likes_per_post)} />
              <Metric label="Avg Comments / Post" value={formatNumber(m.avg_comments_per_post)} />
              <Metric label="Views / Subscriber" value={m.views_per_subscriber} />
              <Metric label="Videos Tracked" value={m.videos_tracked} />
            </div>

            {/* Monetization */}
            <div className="border border-[#A3E635]/30 bg-[#050505] p-4"
              style={{ boxShadow: "0 0 12px rgba(163, 230, 53,0.06)" }}>
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-3">
                <CurrencyDollarIcon size={15} weight="fill" /> Monetization Readiness
              </div>

              <div className="flex items-center justify-between font-mono text-xs text-[#A0A0AB] mb-1.5">
                <span className="flex items-center gap-1.5">
                  {mon.eligible_subs && <CheckCircleIcon size={13} className="text-[#A3E635]" weight="fill" />}
                  {formatNumber(mon.current_subs)} / {formatNumber(mon.ypp_subs_required)} subs for YPP
                </span>
                <span style={{ color: mon.eligible_subs ? "#A3E635" : "#A0A0AB" }}>{mon.subs_progress_pct}%</span>
              </div>
              <div className="h-2 bg-[#141417] overflow-hidden mb-4">
                <div className="h-full transition-all duration-700"
                  style={{ width: `${mon.subs_progress_pct}%`, background: mon.eligible_subs ? "#A3E635" : "#16C784" }} />
              </div>

              <div className="flex items-center justify-between font-mono text-xs text-[#A0A0AB] mb-1.5">
                <span className="flex items-center gap-1.5">
                  {m.watch_time.eligible_hours
                    ? <CheckCircleIcon size={13} className="text-[#A3E635]" weight="fill" />
                    : <HourglassMediumIcon size={13} className="text-[#A0A0AB]" />}
                  ~{formatNumber(m.watch_time.est_total_hours)} / {formatNumber(m.watch_time.ypp_hours_required)} watch hrs (est.)
                </span>
                <span style={{ color: m.watch_time.eligible_hours ? "#A3E635" : "#A0A0AB" }}>{m.watch_time.hours_progress_pct}%</span>
              </div>
              <div className="h-2 bg-[#141417] overflow-hidden mb-4">
                <div className="h-full transition-all duration-700"
                  style={{ width: `${m.watch_time.hours_progress_pct}%`, background: m.watch_time.eligible_hours ? "#A3E635" : "#16C784" }} />
              </div>

              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Est. Monthly Revenue*</div>
                  <div className="font-rajdhani font-bold text-lg md:text-xl text-[#A3E635]">
                    ${formatNumber(mon.est_monthly_revenue_low)}–${formatNumber(mon.est_monthly_revenue_high)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Subs To Go</div>
                  <div className="font-rajdhani font-bold text-lg md:text-xl text-white">
                    {mon.eligible_subs ? "Eligible ✓" : formatNumber(mon.subs_remaining)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Watch Hrs To Go</div>
                  <div className="font-rajdhani font-bold text-lg md:text-xl text-white">
                    {m.watch_time.eligible_hours ? "Eligible ✓" : `~${formatNumber(m.watch_time.hours_remaining)}`}
                  </div>
                </div>
              </div>

              {/* Monetization ETA */}
              {mon.eta_label && (
                <div data-testid="monetization-eta" className="flex items-center gap-3 border border-[#A3E635]/30 bg-[#0C0C0E] p-3 mb-3">
                  <div className="w-9 h-9 rounded-full border border-[#A3E635]/50 flex items-center justify-center shrink-0">
                    <RocketLaunchIcon size={18} className="text-[#A3E635]" weight="fill" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Monetization ETA</div>
                    <div className="font-rajdhani font-bold text-xl text-[#A3E635] leading-tight">{mon.eta_label}</div>
                  </div>
                  {mon.eta_bottleneck && (
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Gated by</div>
                      <div className="font-rajdhani font-bold text-sm text-white capitalize">{mon.eta_bottleneck}</div>
                      <div className="font-mono text-[10px] text-[#5a5a63]">
                        +{formatNumber(mon.est_subs_per_month)} subs · +{formatNumber(mon.est_watch_hours_per_month)} hrs / mo
                      </div>
                    </div>
                  )}
                </div>
              )}
              {mon.eta_detail && (
                <p data-testid="monetization-eta-detail" className="text-xs text-[#A0A0AB] leading-relaxed mb-3 border-l-2 border-[#A3E635]/40 pl-3">
                  <span className="text-[#A3E635] font-bold">Why:</span> {mon.eta_detail}
                </p>
              )}

              {/* Growth & revenue projection with what-if output slider */}
              {havePerPost && (
                <div data-testid="monetization-projection" className="border border-[#16C784]/30 bg-[#0C0C0E] p-3 md:p-4 mb-3">
                  <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-2">
                    <TrendUpIcon size={13} weight="fill" /> Growth &amp; Revenue Projection
                  </div>

                  {/* What-if output slider */}
                  <div className="mb-3" data-testid="output-scenarios">
                    <div className="flex items-baseline justify-between mb-2">
                      <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-[#5a5a63]">What if I post…</div>
                      <div className="font-rajdhani font-bold text-lg text-[#A3E635]" data-testid="output-slider-value">
                        {fmtWeek(perWeek)} <span className="text-xs text-[#A0A0AB]">/ week</span>
                        <span className="font-mono text-[10px] text-[#5a5a63] ml-2">(~{fmtWeek(targetPerMonth)}/mo)</span>
                      </div>
                    </div>
                    <Slider
                      data-testid="output-slider"
                      value={[perWeek]}
                      min={0}
                      max={sliderMax}
                      step={0.5}
                      onValueChange={(v) => setSliderPerWeek(v[0])}
                      className="my-2 [&>span:first-child]:bg-[#16C784]/20 [&>span:first-child>span]:bg-[#16C784] [&_[role=slider]]:border-[#16C784] [&_[role=slider]]:bg-[#16C784]"
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {currentPerWeek > 0 && (
                        <button
                          data-testid="scenario-current"
                          onClick={() => setSliderPerWeek(currentPerWeek)}
                          className="font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] transition-colors duration-200"
                        >
                          Current ~{fmtWeek(currentPerWeek)}/wk
                        </button>
                      )}
                      {recPerWeek && (
                        <button
                          data-testid="scenario-rec"
                          onClick={() => setSliderPerWeek(recPerWeek)}
                          className="font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] transition-colors duration-200"
                        >
                          Recommended ~{fmtWeek(recPerWeek)}/wk
                        </button>
                      )}
                      {recPerWeek && (
                        <button
                          data-testid="scenario-rec2x"
                          onClick={() => setSliderPerWeek(recPerWeek * 2)}
                          className="font-rajdhani font-bold uppercase tracking-wider text-[10px] px-2.5 py-1 border border-[#222226] text-[#A0A0AB] hover:border-[#16C784]/50 hover:text-[#16C784] transition-colors duration-200"
                        >
                          2× recommended
                        </button>
                      )}
                    </div>
                  </div>

                  {projection ? (
                    <>
                      <div data-testid="projection-readout" className="mb-3 border border-[#222226] bg-[#050505] p-2.5">
                        <p className="font-mono text-[10px] text-[#A0A0AB] leading-relaxed">
                          At ~<span className="text-[#A3E635] font-bold">{fmtWeek(perWeek)}/week</span> → monetize in{" "}
                          <span className="text-[#A3E635] font-bold">{fmtMonths(projection.monetizeMonth)}</span>
                          {projection.bottleneck && <span className="text-[#5a5a63]"> ({projection.bottleneck} is the gate)</span>}
                          , then 2 years of revenue potential.
                        </p>
                        {basePace && currentPerWeek > 0 && Math.abs(perWeek - currentPerWeek) > 0.05 && monthsDelta !== 0 && (
                          <p className="font-mono text-[10px] mt-1" style={{ color: monthsDelta > 0 ? "#16C784" : "#FF4D4D" }}>
                            {monthsDelta > 0
                              ? `▲ ~${monthsDelta} month${monthsDelta > 1 ? "s" : ""} SOONER`
                              : `▼ ~${Math.abs(monthsDelta)} month${Math.abs(monthsDelta) > 1 ? "s" : ""} SLOWER`}
                            {" "}than your current ~{fmtWeek(currentPerWeek)}/week pace ({fmtMonths(basePace.monetizeMonth)}).
                          </p>
                        )}
                      </div>
                      <ResponsiveContainer width="100%" height={250}>
                        <ComposedChart data={projection.data} margin={{ top: 8, right: 6, left: -14, bottom: 0 }}>
                          <defs>
                            <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#FFB800" stopOpacity={0.45} />
                              <stop offset="100%" stopColor="#FFB800" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1e" vertical={false} />
                          <XAxis dataKey="label" stroke="#5a5a63" tick={{ fontSize: 10, fontFamily: "monospace" }} interval="preserveStartEnd" tickMargin={6} />
                          <YAxis yAxisId="left" stroke="#5a5a63" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => `${v}%`} />
                          <YAxis yAxisId="right" orientation="right" stroke="#FFB800" tick={{ fontSize: 10, fontFamily: "monospace" }} tickFormatter={(v) => `$${formatNumber(v)}`} width={44} />
                          <Tooltip content={<ProjTooltip />} />
                          <ReferenceLine yAxisId="left" y={100} stroke="#3a3a40" strokeDasharray="4 4" label={{ value: "YPP goal", fill: "#5a5a63", fontSize: 9, position: "insideTopLeft" }} />
                          <ReferenceLine yAxisId="left" x={projection.monetizeLabel} stroke="#A3E635" strokeDasharray="4 4" label={{ value: "Monetized", fill: "#A3E635", fontSize: 9, position: "top" }} />
                          <Area yAxisId="right" type="monotone" dataKey="revenue" name="Est. $/mo" stroke="#FFB800" strokeWidth={2} fill="url(#revGrad)" connectNulls={false} dot={false} />
                          <Line yAxisId="left" type="monotone" dataKey="subsPct" name="Subs → 1K" stroke="#16C784" strokeWidth={2} dot={false} />
                          <Line yAxisId="left" type="monotone" dataKey="hoursPct" name="Watch hrs → 4K" stroke="#60A5FA" strokeWidth={2} strokeDasharray="5 3" dot={false} />
                          <Legend wrapperStyle={{ fontSize: 10, fontFamily: "monospace", paddingTop: 6 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                      <p className="font-mono text-[9px] text-[#5a5a63] mt-1">
                        Straight-line projection from your average per-video performance × the output above — real growth compounds and has diminishing returns. Directional guidance only.
                      </p>
                    </>
                  ) : (
                    <p data-testid="projection-empty" className="font-mono text-[11px] text-[#5a5a63] py-6 text-center">
                      Slide your posting output up to project a path to monetization.
                    </p>
                  )}
                </div>
              )}

              {ai?.monetization_verdict && <p className="text-sm text-[#d4d4d8]">{ai.monetization_verdict}</p>}

              {(ai?.monetize_faster || []).length > 0 && (
                <div className="mt-3">
                  <button
                    data-testid="monetize-faster-toggle"
                    onClick={() => setShowMonetize((s) => !s)}
                    className="w-full flex items-center justify-between gap-2 bg-[#16C784]/10 hover:bg-[#16C784]/20 border border-[#16C784]/40 px-3 py-2.5 transition-colors duration-200"
                  >
                    <span className="flex items-center gap-2 font-rajdhani font-bold uppercase tracking-wider text-sm text-[#16C784]">
                      <RocketLaunchIcon size={16} weight="fill" /> How can I monetize faster?
                    </span>
                    <CaretDownIcon size={16} className={`text-[#16C784] transition-transform duration-200 ${showMonetize ? "rotate-180" : ""}`} />
                  </button>
                  {showMonetize && (
                    <ol data-testid="monetize-faster-list" className="mt-3 space-y-2.5">
                      {ai.monetize_faster.map((s, i) => (
                        <li key={i} data-testid={`monetize-faster-${i}`} className="flex gap-3 border border-[#222226] bg-[#050505] p-3">
                          <span className="font-rajdhani font-bold text-lg text-[#16C784] leading-none shrink-0">{String(i + 1).padStart(2, "0")}</span>
                          <span className="text-sm text-[#d4d4d8]">{s}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}

              <p className="font-mono text-[10px] text-[#5a5a63] mt-2">
                * Est. $1–$6 RPM once monetized. Watch hours &amp; ETA estimated from recent output and this channel's view-to-subscriber rate (YouTube doesn't expose exact watch time publicly).
              </p>
            </div>

            {/* Best time to post */}
            <div data-testid="posting-window" className="border border-[#222226] bg-[#050505] p-4">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-4">
                <CalendarBlankIcon size={15} weight="fill" /> Optimal Posting Window
                <span className="text-[#5a5a63] normal-case tracking-normal font-mono text-[10px]">· {tzLabel}</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="border border-[#222226] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] flex items-center gap-1"><CalendarBlankIcon size={12} /> Best Day</div>
                  <div className="font-rajdhani font-bold text-2xl text-[#16C784] mt-1">{posting.best_day || "—"}</div>
                </div>
                <div className="border border-[#222226] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] flex items-center gap-1"><ClockIcon size={12} /> Best Time</div>
                  <div className="font-rajdhani font-bold text-2xl text-[#16C784] mt-1">{posting.best_hour || "—"}</div>
                </div>
              </div>

              {/* Recommended schedule callout */}
              <div data-testid="recommended-schedule" className="border border-[#A3E635]/30 bg-[#0C0C0E] p-3 mb-4"
                style={{ boxShadow: "0 0 10px rgba(163, 230, 53,0.06)" }}>
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-2">
                  <TargetIcon size={13} weight="fill" /> Recommended Schedule
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="flex items-start gap-2">
                    <ClockIcon size={16} className="text-[#16C784] shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Optimal Time</div>
                      <div className="font-rajdhani font-bold text-base text-white">
                        {posting.best_day ? `${posting.best_day}s · ${posting.best_hour}` : "—"}
                        <span className="font-mono text-[10px] text-[#5a5a63] ml-1">({tzLabel})</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <RepeatIcon size={16} className="text-[#16C784] shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Optimal Frequency</div>
                      <div className="font-rajdhani font-bold text-base text-white">
                        {ai?.schedule?.optimal_frequency || <span className="text-[#5a5a63] font-mono text-xs">Re-run scan to update</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <TimerIcon size={16} className="text-[#16C784] shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Optimal Length</div>
                      <div className="font-rajdhani font-bold text-base text-white">{m.length?.best_band || "—"}</div>
                    </div>
                  </div>
                </div>
                {m.length?.best_band && (
                  <p className="text-xs text-[#d4d4d8] mt-3">
                    Your <span className="text-[#A3E635]">{m.length.best_band}</span> videos average{" "}
                    {formatNumber(m.length.best_band_avg_views)} views — your strongest length. Top videos run ~{m.length.top_avg_length} (channel avg {m.length.avg_length}).
                  </p>
                )}
                {ai?.schedule?.frequency_rationale && (
                  <p className="text-xs text-[#d4d4d8] mt-2">{ai.schedule.frequency_rationale}</p>
                )}
                {ai?.schedule?.consistency_tip && (
                  <p className="text-xs text-[#A0A0AB] mt-1.5"><span className="text-[#A3E635]">TIP:</span> {ai.schedule.consistency_tip}</p>
                )}
              </div>

              {/* Avg views by video length */}
              {m.length?.bands?.some((b) => b.count > 0) && (
                <div className="mb-4" data-testid="length-bands">
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-2">Avg Views By Video Length</div>
                  <div className="space-y-1.5">
                    {(() => {
                      const maxAvg = Math.max(...m.length.bands.map((b) => b.avg_views), 1);
                      return m.length.bands.map((b) => (
                        <div key={b.band} className="flex items-center gap-3" data-testid={`length-band-${b.band.replace(/\s+/g, "")}`}>
                          <span className="font-mono text-[11px] text-[#A0A0AB] w-16 shrink-0">{b.band}</span>
                          <div className="flex-1 h-4 bg-[#141417] overflow-hidden">
                            <div className="h-full transition-all duration-700"
                              style={{
                                width: `${(b.avg_views / maxAvg) * 100}%`,
                                background: b.band === m.length.best_band ? "#16C784" : "#A3E635",
                                opacity: b.count ? 1 : 0.15,
                              }} />
                          </div>
                          <span className="font-mono text-[10px] text-[#5a5a63] w-24 text-right shrink-0">
                            {b.count ? `${formatNumber(b.avg_views)} · ${b.count}v` : "no data"}
                          </span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Day-of-week avg views bars */}
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-wider text-[#5a5a63]">Avg Views By Day</div>
                <div className="flex items-center gap-3 font-mono text-[9px] uppercase tracking-wider">
                  <span className="flex items-center gap-1 text-[#16C784]"><span className="w-2.5 h-2.5 inline-block bg-[#16C784]" /> Best day</span>
                  <span className="flex items-center gap-1 text-[#A0A0AB]"><span className="w-2.5 h-2.5 inline-block bg-[#A3E635]" /> Other days</span>
                </div>
              </div>
              <div className="space-y-1.5 mb-4">
                {(() => {
                  const maxAvg = Math.max(...posting.day_stats.map((d) => d.avg_views), 1);
                  return posting.day_stats.map((d) => (
                    <div key={d.day} className="flex items-center gap-3" data-testid={`day-stat-${d.day}`}>
                      <span className="font-mono text-[11px] text-[#A0A0AB] w-9 shrink-0">{d.day}</span>
                      <div className="flex-1 h-4 bg-[#141417] overflow-hidden">
                        <div
                          className="h-full transition-all duration-700"
                          style={{
                            width: `${(d.avg_views / maxAvg) * 100}%`,
                            background: d.day === posting.best_day ? "#16C784" : "#A3E635",
                            opacity: d.count ? 1 : 0.15,
                          }}
                        />
                      </div>
                      <span className="font-mono text-[10px] text-[#5a5a63] w-20 text-right shrink-0">
                        {d.count ? formatNumber(d.avg_views) : "no data"}
                      </span>
                    </div>
                  ));
                })()}
              </div>

              {posting.top_slots?.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-2">When Your Top Videos Went Live</div>
                  <div className="space-y-1.5">
                    {posting.top_slots.map((t, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs" data-testid={`top-slot-${i}`}>
                        <span className="font-mono text-[10px] text-[#16C784] w-24 shrink-0">{t.day} · {t.time}</span>
                        <span className="text-[#d4d4d8] truncate flex-1">{t.title}</span>
                        <span className="font-mono text-[10px] text-[#A0A0AB] shrink-0">{formatNumber(t.views)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="font-mono text-[10px] text-[#5a5a63] mt-3">
                Times shown in your local time ({tzLabel}) · ranked by average views per day.
              </p>
            </div>

            {/* Content Playbook */}
            {ai?.niche_analysis && (
              <div data-testid="niche-analysis" className="border border-[#16C784]/30 bg-[#050505] p-4"
                style={{ boxShadow: "0 0 12px rgba(22,199,132,0.06)" }}>
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#16C784] mb-3">
                  <CompassIcon size={15} weight="fill" /> Niche & Revenue Opportunity
                </div>

                <div className="grid sm:grid-cols-3 gap-3 mb-4">
                  <div className="border border-[#222226] bg-[#0C0C0E] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">Niche</div>
                    <div data-testid="niche-name" className="font-rajdhani font-bold text-base text-white leading-tight">{ai.niche_analysis.niche}</div>
                    {ai.niche_analysis.sub_niche && <div className="font-mono text-[10px] text-[#A0A0AB] mt-0.5">{ai.niche_analysis.sub_niche}</div>}
                  </div>
                  <div className="border bg-[#0C0C0E] p-3" style={{ borderColor: potColor(ai.niche_analysis.revenue_opportunity) + "55" }}>
                    <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">Revenue Opportunity</div>
                    <div data-testid="niche-opportunity" className="font-rajdhani font-bold text-lg flex items-center gap-1.5" style={{ color: potColor(ai.niche_analysis.revenue_opportunity) }}>
                      <TrendUpIcon size={16} weight="fill" /> {ai.niche_analysis.revenue_opportunity}
                    </div>
                  </div>
                  <div className="border border-[#A3E635]/30 bg-[#0C0C0E] p-3">
                    <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-1">Est. RPM · niche benchmark</div>
                    <div data-testid="niche-rpm" className="font-rajdhani font-bold text-lg text-[#A3E635] flex items-center gap-1.5">
                      <CurrencyDollarIcon size={16} weight="fill" /> {ai.niche_analysis.estimated_rpm}
                    </div>
                  </div>
                </div>

                {ai.niche_analysis.rpm_rationale && <p className="text-xs text-[#A0A0AB] mb-2 leading-relaxed">{ai.niche_analysis.rpm_rationale}</p>}
                {ai.niche_analysis.revenue_verdict && (
                  <p data-testid="niche-verdict" className="text-sm text-[#d4d4d8] leading-relaxed mb-4 border-l-2 border-[#16C784]/40 pl-3">{ai.niche_analysis.revenue_verdict}</p>
                )}

                <div className="grid md:grid-cols-2 gap-5">
                  {(ai.niche_analysis.best_revenue_streams || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A3E635] mb-2">
                        <StorefrontIcon size={14} weight="fill" /> Best Revenue Streams
                      </div>
                      <ul className="space-y-1.5">
                        {ai.niche_analysis.best_revenue_streams.map((s, i) => (
                          <li key={i} data-testid={`revenue-stream-${i}`} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#A3E635]">▸</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(ai.niche_analysis.high_value_angles || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#16C784] mb-2">
                        <TargetIcon size={14} weight="fill" /> High-CPM Angles
                      </div>
                      <ul className="space-y-1.5">
                        {ai.niche_analysis.high_value_angles.map((s, i) => (
                          <li key={i} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#16C784]">▸</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {(ai.niche_analysis.competition_level || ai.niche_analysis.saturation || ai.niche_analysis.channel_standing) && (
                  <div data-testid="niche-competition" className="mt-4 border-t border-[#1a1a1e] pt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FFB800]">
                        <UsersThreeIcon size={14} weight="fill" /> Niche Competition
                      </div>
                      {ai.niche_analysis.competition_level && (
                        <span data-testid="niche-competition-level" className="font-rajdhani font-bold text-xs px-2 py-0.5 border rounded-sm"
                          style={{ color: compColor(ai.niche_analysis.competition_level), borderColor: compColor(ai.niche_analysis.competition_level) + "66" }}>
                          {ai.niche_analysis.competition_level} saturation
                        </span>
                      )}
                    </div>
                    {ai.niche_analysis.saturation && <p className="text-sm text-[#d4d4d8] leading-relaxed mb-2">{ai.niche_analysis.saturation}</p>}
                    {ai.niche_analysis.channel_standing && (
                      <p data-testid="niche-standing" className="text-sm text-[#d4d4d8] leading-relaxed border-l-2 border-[#FFB800]/40 pl-3 mb-3">
                        <span className="text-[#FFB800] font-bold">Where you stand: </span>{ai.niche_analysis.channel_standing}
                      </p>
                    )}
                    {(ai.niche_analysis.similar_creators || []).length > 0 && (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[10px] uppercase tracking-wider text-[#5a5a63]">Similar creators:</span>
                        {ai.niche_analysis.similar_creators.map((s, i) => (
                          <span key={i} className="font-mono text-[10px] text-[#A0A0AB] border border-[#222226] bg-[#0C0C0E] px-2 py-0.5">{s}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <RevenueCalculator rpmLow={ai.niche_analysis.rpm_low} rpmHigh={ai.niche_analysis.rpm_high} defaultViews={100000} />
              </div>
            )}

            {ai?.content_playbook && (
              <div data-testid="content-playbook" className="border border-[#16C784]/30 bg-[#050505] p-4"
                style={{ boxShadow: "0 0 12px rgba(22, 199, 132,0.06)" }}>
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#16C784] mb-3">
                  <FilmSlateIcon size={15} weight="fill" /> Content Playbook · What's Working
                </div>

                {ai.content_playbook.winning_formula && (
                  <div className="flex items-start gap-2 mb-4 bg-[#0C0C0E] border border-[#A3E635]/30 p-3">
                    <FireIcon size={16} className="text-[#A3E635] shrink-0 mt-0.5" weight="fill" />
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#A3E635]">Winning Formula: </span>
                      <span className="text-sm text-[#d4d4d8]">{ai.content_playbook.winning_formula}</span>
                    </div>
                  </div>
                )}

                {(ai.content_playbook.top_content_types || []).length > 0 && (
                  <div className="mb-4">
                    <div className="text-[10px] uppercase tracking-wider text-[#5a5a63] mb-2">Top Content Types</div>
                    <div className="space-y-2">
                      {ai.content_playbook.top_content_types.map((t, i) => (
                        <div key={i} data-testid={`content-type-${i}`} className="border border-[#222226] p-3">
                          <div className="font-rajdhani font-bold text-base text-white flex items-center gap-2">
                            <span className="font-mono text-[10px] text-[#16C784]">{String(i + 1).padStart(2, "0")}</span>
                            {t.type}
                          </div>
                          {t.evidence && <p className="text-xs text-[#A0A0AB] mt-1">{t.evidence}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A3E635] mb-2">
                      <CopySimpleIcon size={14} weight="fill" /> Replicate These
                    </div>
                    <ul className="space-y-1.5">
                      {(ai.content_playbook.replicate || []).map((s, i) => (
                        <li key={i} data-testid={`replicate-${i}`} className="text-sm text-[#d4d4d8] flex gap-2">
                          <span className="text-[#A3E635]">▸</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                  {(ai.content_playbook.avoid || []).length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A0A0AB] mb-2">
                        <ProhibitIcon size={14} /> Dial Back
                      </div>
                      <ul className="space-y-1.5">
                        {ai.content_playbook.avoid.map((s, i) => (
                          <li key={i} className="text-sm text-[#A0A0AB] flex gap-2"><span className="text-[#5a5a63]">×</span>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Strengths / Opportunities */}
            {ai && (
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#A3E635] mb-2">
                    <MedalIcon size={14} weight="fill" /> Strengths
                  </div>
                  <ul className="space-y-1.5">
                    {(ai.strengths || []).map((s, i) => (
                      <li key={i} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#A3E635]">+</span>{s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#16C784] mb-2">
                    <TrendUpIcon size={14} weight="fill" /> Opportunities
                  </div>
                  <ul className="space-y-1.5">
                    {(ai.opportunities || []).map((s, i) => (
                      <li key={i} className="text-sm text-[#d4d4d8] flex gap-2"><span className="text-[#16C784]">→</span>{s}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {ai?.quickest_path_to_1k && (
              <div className="flex items-start gap-2 border-l-2 border-[#16C784] pl-3">
                <TargetIcon size={16} className="text-[#16C784] shrink-0 mt-0.5" weight="fill" />
                <div>
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#16C784]">Fastest Path to 1K: </span>
                  <span className="text-sm text-[#d4d4d8]">{ai.quickest_path_to_1k}</span>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ChannelAnalysis;
